import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar } from "@material-ui/core";
import { CustomAlert } from "components/commons/alertMessage/customAlert";

// Kiểu severity cho alert
export type AlertSeverity = "success" | "error" | "warning" | "info";

// Options để show notification
export interface AlertOptions {
  content: string;
  severity?: AlertSeverity;
  countDown?: number; // ms
}

// Kiểu context
interface NotificationContextType {
  notify: (options: AlertOptions) => void;
  close: () => void;
}

// Tạo context private
const NotificationContext = createContext<NotificationContextType | null>(null);

// Hook public để dùng trong component
export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within NotificationProvider");
  return ctx;
};

// Provider
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AlertOptions | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [displayContent, setDisplayContent] = useState<string>("");

  const notify = useCallback((opts: AlertOptions) => {
    setOptions(opts);
    setOpen(true);

    if (opts.countDown && opts.countDown > 0) {
      const seconds = Math.floor(opts.countDown / 1000);
      setCountdown(seconds);
      setDisplayContent(`${opts.content} (${seconds}s)`);

      const interval = setInterval(() => {
        setCountdown((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            clearInterval(interval);
            setOpen(false);
            return 0;
          }
          setDisplayContent(`${opts.content} (${next}s)`);
          return next;
        });
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setCountdown(0);
      setDisplayContent(opts.content);
    }
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, close }}>
      {children}
      {options && (
        <Snackbar
          open={open}
          onClose={close}
          anchorOrigin={{ vertical: "top", horizontal: window.innerWidth > 765 ? "right" : "center" }}
          autoHideDuration={options.countDown ? null : 3000}
        >
          <CustomAlert severity={options.severity || "info"} onClose={close}>
            {countdown ? displayContent : options.content}
          </CustomAlert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};
